import React, { useState, useEffect } from 'react';
import './index.css';
import { Formik, Form, ErrorMessage } from 'formik';
import { getIcon, Loader, SuccessModal } from '@ohif/ui';
import { _commonUrls, _pacsUrls } from '@custom/Utils/constants/apiUrls';
import { capitalizeWords, customStyle } from '@custom/Utils/utils';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '@custom/store/store';
import { servicesManager } from '../../app/src/App';
import { useTheme } from '../../ui/src/contextProviders';
import addEditPrice from '@custom/rtk-apis/price/addPrice';
import editPrice from '@custom/rtk-apis/price/editPrice';
import addEditModalityStudy from '@custom/rtk-apis/modalityStudyRegion/addModalityStudy';
import getModalityStudy from '@custom/rtk-apis/modalityStudyRegion/getModalityStudy';
import { FormControl, MenuItem, Select } from '@mui/material';
import getPrice from '@custom/rtk-apis/price/getPrice';
import { closePricingModal } from '@custom/store/slices/price/addEditPrice';

interface AddEditPricingProps {
  setShowAddForm: (value: boolean) => void;
  priceModalityData?: any;
}

const AddEditPricing = ({ setShowAddForm, priceModalityData }: AddEditPricingProps) => {
  const dispatch = useDispatch<AppDispatch>();
  const { status, loading, result, msg } = useSelector((state: RootState) => state?.addEditPriceSliceResponse);
  const { status: modalityStudyStatus, loading: modalityStudyLoading, result: modalityStudyResult, msg: modalityStudyMsg } = useSelector((state: RootState) => state?.addEditModalityStudySliceResponse);
  const modalityStudyResponse = useSelector((state: RootState) => state?.getModalityStudySliceResponse);

  const isDarkMode = useTheme();
  const [edit, setIsEdit] = useState(false);
  const [modal, setModal] = useState(false);
  const [modalityModal, setModalityModal] = useState(false);
  const [modalityList, setModality] = useState<any>([]);
  const [reportStatus, setReportStatus] = useState<any>([]);
  const [modalMessage, setModalMessage] = useState('');
  const { uiNotificationService } = servicesManager.services;
  const [isAddingRegion, setIsAddingRegion] = useState(false);
  const [newRegion, setNewRegion] = useState("");
  const [selectedRegion, setSelectedRegion] = useState(priceModalityData?.studyRegion || "");
  const [isAddingModality, setIsAddingModality] = useState(false);
  const [newModality, setNewModality] = useState("");
  const [selectedModality, setSelectedModality] = useState(priceModalityData?.modality || "");
  useEffect(() => {
    dispatch(getModalityStudy());
  }, [dispatch]);

  useEffect(() => {
    setIsEdit(!!priceModalityData);
  }, [priceModalityData]);

  useEffect(() => {
    if (modalityStudyResponse?.status === 'SUCCESS') {
      const modalities = modalityStudyResponse?.result?.modalities;
      const studyRegions = modalityStudyResponse?.result?.studyRegions;
      const uniqueModalities = Array.from(new Set(modalities));
      const uniqueRegions = Array.from(new Set(studyRegions));

      const modalityList = uniqueModalities?.map(modality => ({
        label: modality,
        value: modality
      }));

      setModality(prevState => ({
        ...prevState,
        modalities: modalityList
      }));

      const studyList = uniqueRegions?.map(studyRegion => ({
        label: studyRegion,
        value: studyRegion
      }));

      setReportStatus(prevState => ({
        ...prevState,
        studyRegions: studyList
      }));
    }
  }, [modalityStudyResponse?.status]);

  const handleModalityChange = (event) => {
    const value = event.target.value;
    if (value === "add-modality") {
      setIsAddingModality(true);
    } else {
      setSelectedModality(value);
    }
  };

  const handleRegionChange = (event) => {
    const value = event.target.value;
    if (value === "add-study-region") {
      setIsAddingRegion(true);
    } else {
      setSelectedRegion(value);
    }
  };

  const saveNewModality = () => {
    if (newModality.trim() === "") return;
    const updatedModalityList = {
      ...modalityList,
      modalities: [
        ...(modalityList?.modalities || []),
        { value: newModality.toLowerCase(), label: newModality },
      ],
    };

    setModality(updatedModalityList);
    handleAddModality("modality", newModality);
    setSelectedModality(newModality);
    setNewModality("");
    setIsAddingModality(false);
    dispatch(getModalityStudy());
  };

  const saveNewRegion = () => {
    if (newRegion.trim() === "") return;
    const updatedRegionList = {
      ...reportStatus,
      studyRegions: [
        ...(reportStatus?.studyRegions || []),
        { value: newRegion.toLowerCase(), label: newRegion },
      ],
    };

    setReportStatus(updatedRegionList);
    handleAddModality("studyRegion", newRegion);
    setSelectedRegion(newRegion);
    setNewRegion("");
    setIsAddingRegion(false);
    dispatch(getModalityStudy());
  };

  const handleSave = async (values) => {
    const price = parseFloat(values.price);
    if (isNaN(price)) {
      uiNotificationService.show({
        title: "Error",
        type: "error",
        message: "Price must be a valid number",
        position: "bottomRight",
      });
      return;
    }

    if (edit) {
      const payload = {
        modality: selectedModality,
        studyRegion: selectedRegion,
        price: price,
        id: priceModalityData?.id,
      };
      try {
        const response = await dispatch(editPrice(payload));
        if (response.meta.requestStatus === 'fulfilled') {
          setModal(true);
          await Promise.all([
            dispatch(getPrice({ page: 1, limit: 30 , modality: '', studyRegion: '' })),
            dispatch(getModalityStudy()),
          ]);
          setTimeout(() => {
            setModal(false);
            setShowAddForm(false);
          }, 1000);
        } else {
          throw new Error(msg || "Failed to update price");
        }
      } catch (error) {
        uiNotificationService.show({
          title: "Error",
          type: "error",
          message: error.message || "Failed to update price",
          position: "bottomRight",
        });
      }
    } else {
      const priceData = {
        modalityPrices: [{
          value: selectedModality,
          studyRegion: selectedRegion,
          price: price,
        }],
      };
      try {
        const response = await dispatch(addEditPrice(priceData));
        if (response.meta.requestStatus === 'fulfilled') {
          setModal(true);
          await Promise.all([
            dispatch(getPrice({ page: 1, limit: 30, modality: '', studyRegion: '' })),
            dispatch(getModalityStudy()),
          ]);
          setTimeout(() => {
            setModal(false);
            setShowAddForm(false);
          }, 1000);
        } else {
          throw new Error(msg || "Failed to add price");
        }
      } catch (error) {
        uiNotificationService.show({
          title: "Error",
          type: "error",
          message: error.message || "Failed to add price",
          position: "bottomRight",
        });
      }
    }
  };

  const initialValues = {
    _id: priceModalityData?._id || '',
    modality: priceModalityData?.modality || '',
    studyRegion: priceModalityData?.studyRegion || '',
    price: priceModalityData?.price || 0,
  };

  const handleAddModality = (key, value) => {
    let payload;
    if (key === 'modality') {
      payload = { modality: value };
      setNewModality(value);
    } else if (key === 'studyRegion') {
      payload = { studyRegion: value };
      setNewRegion(value);
    }
    dispatch(addEditModalityStudy(payload));
    const message = 'Data added successfully.';
    setModalMessage(message);
    setModalityModal(true);
  };

  return (
    <>
      <div className='pricing page__body'>
        <div className='details'>
          <div className='form form--innersec'>
            <div className="mb-3">
              <button
                className=''
                onClick={() => setShowAddForm(false)}
                style={{ display: 'flex', alignItems: 'center' }}
              >
                {getIcon('icon-toolbar-back')}
                Back
              </button>
            </div>
            <section className='form__section'>
              <h1 className='form__section__title'>Price Details</h1>
              <Formik
                initialValues={initialValues}
                onSubmit={handleSave}
                enableReinitialize
              >
                {({
                  errors,
                  values,
                  handleChange,
                  handleBlur,
                  touched,
                  setFieldValue,
                  setFieldTouched,
                }) => {
                  const isFormValid =
                    selectedModality &&
                    selectedRegion &&
                    values.price > 0;

                  return (
                    <Form>
                      <div className="formRow formRow__3">
                        <div className='formCol'>
                          <div className='form__Field'>
                            <label className="form__label">
                              Modality
                              <span className="required-asterisk">*</span>
                            </label>
                            <FormControl fullWidth size="small" variant="outlined">
                              {isAddingModality ? (
                                <div style={{ display: "flex", alignItems: "center" }}>
                                  <input
                                    type="text"
                                    value={newModality}
                                    onChange={(e) => setNewModality(e.target.value)}
                                    placeholder="Enter new modality"
                                    className="form__input"
                                    style={{
                                      flexGrow: 1,
                                      padding: "8px",
                                      border: "1px solid #ccc",
                                      borderRadius: "2rem",
                                      height: '10%'
                                    }}
                                  />
                                  <button
                                    onClick={saveNewModality}
                                    className='btn btn__primary ml-1'
                                  >
                                    Save
                                  </button>
                                  <button
                                    onClick={() => setIsAddingModality(false)}
                                    className='btn btn__white ml-2'
                                  >
                                    Cancel
                                  </button>
                                </div>
                              ) : (
                                <Select
                                  value={selectedModality}
                                  onChange={handleModalityChange}
                                  displayEmpty
                                  className="form__input form__input--sm"
                                  style={{ padding: "0px", borderRadius: '2rem' }}
                                  disabled={edit}
                                >
                                  <MenuItem value="" disabled>
                                    Modality
                                  </MenuItem>
                                  <MenuItem value="add-modality" className="menu-item--add-modality">
                                    {getIcon("icon-add")} <span style={{ marginLeft: "4px" }}>Add Modality</span>
                                  </MenuItem>
                                  {modalityList?.modalities?.map((option) => (
                                    <MenuItem key={option.value} value={option.label}>
                                      {capitalizeWords(option.label)}
                                    </MenuItem>
                                  ))}
                                </Select>
                              )}
                              {!selectedModality && !newModality && errors.modality && touched.modality && (
                                <div style={{ color: "red", fontSize: "11.8px" }}>
                                  {errors.modality}
                                </div>
                              )}
                            </FormControl>
                          </div>
                        </div>
                        <div className="formCol">
                          <div className="form__Field">
                            <label className="form__label">
                              Study Region
                              <span className="required-asterisk">*</span>
                            </label>
                            <FormControl fullWidth size="small" variant="outlined">
                              {isAddingRegion ? (
                                <div style={{ display: "flex", alignItems: "center" }}>
                                  <input
                                    type="text"
                                    value={newRegion}
                                    onChange={(e) => setNewRegion(e.target.value)}
                                    placeholder="Enter new study region"
                                    className="form__input"
                                    style={{
                                      flexGrow: 1,
                                      padding: "8px",
                                      border: "1px solid #ccc",
                                      borderRadius: "2rem",
                                      height: '10%'
                                    }}
                                  />
                                  <button
                                    onClick={saveNewRegion}
                                    className='btn btn__primary ml-1'
                                  >
                                    Save
                                  </button>
                                  <button
                                    onClick={() => setIsAddingRegion(false)}
                                    className='btn btn__white ml-2'
                                  >
                                    Cancel
                                  </button>
                                </div>
                              ) : (
                                <Select
                                  value={selectedRegion}
                                  onChange={handleRegionChange}
                                  displayEmpty
                                  className="form__input form__input--sm"
                                  style={{ padding: "0px", borderRadius: '2rem' }}
                                  disabled={edit}
                                >
                                  <MenuItem value="" disabled>
                                    Study Region
                                  </MenuItem>
                                  <MenuItem value="add-study-region" className="menu-item--add-study-region">
                                    {getIcon("icon-add")} <span style={{ marginLeft: "4px" }}>Add Study Region</span>
                                  </MenuItem>
                                  {reportStatus?.studyRegions?.map((option) => (
                                    <MenuItem key={option.value} value={option.label}>
                                      {capitalizeWords(option.label)}
                                    </MenuItem>
                                  ))}
                                </Select>
                              )}
                              {!selectedRegion && !newRegion && errors.studyRegion && touched.studyRegion && (
                                <div style={{ color: "red", fontSize: "11.8px" }}>
                                  {errors.studyRegion}
                                </div>
                              )}
                            </FormControl>
                          </div>
                        </div>
                        <div className='formCol'>
                          <div className="form__Field">
                            <label className="form__label">Default Price <span className="required-asterisk">*</span></label>
                            <input
                              type="number"
                              id="price"
                              name="price"
                              placeholder="Enter price"
                              min="1"
                              onChange={(e) => {
                                const price = parseFloat(e.target.value);
                                setFieldValue("price", price > 0 ? price : "");
                              }}
                              onBlur={handleBlur}
                              value={values.price}
                              className="form__input form__input--sm"
                              autoComplete="off"
                            />
                            {values.price <= 0 && touched.price && (
                              <div style={{ color: "red", fontSize: "11.8px" }}>
                                Price must be greater than 0
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className='btn__group'>
                        <button className='btn btn__white' onClick={() => setShowAddForm(false)}>
                          Cancel
                        </button>
                        <button className='btn btn__primary' type='submit' disabled={!isFormValid}>
                          {edit ? 'Update' : 'Save'}
                        </button>
                      </div>
                    </Form>
                  );
                }}
              </Formik>
            </section>
          </div>
        </div>
      </div>
      <SuccessModal
        isSuccessModalOpen={modal}
        title={msg || (edit ? "Price updated successfully." : "Price added successfully.")}
        handleCloseModal={() => setModal(false)}
      />
      <SuccessModal
        isSuccessModalOpen={modalityModal}
        title={modalMessage}
        handleCloseModal={() => setModalityModal(false)}
      />
      <Loader isLoaderOpen={loading} />
    </>
  );
};

export default AddEditPricing;
